import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaExclamationCircle } from 'react-icons/all';
import { FaArrowRight } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import { Address, Location, LocationType, UserProfile } from '../../../../@posturinn/client-react';
import { setActiveModal } from '../../../../actions/modalActions';
import { agreeToGdpr } from '../../../../actions/userProfileActions';
import { DELIVERY_OPTION_MODAL } from '../../../../constants/documentTypes/modals/modals';
import useLocations from '../../../../hooks/useLocations';
import IPaymentReducer from '../../../../interfaces/reducers/paymentReducer';
import IUserProfileReducer from '../../../../interfaces/reducers/userProfileReducer';
import { IStore } from '../../../../reducers';
import Button from '../../../base/button/button';
import { H2 } from '../../../base/typography';
import * as StyledModals from '../../modals/myShipmentsModals.styles';
import * as StyledOnboarding from '../onboarding.styles';

interface Props {
	NewUserProfile: UserProfile;
}

const StepDelivery: FC<Props> = ({ NewUserProfile }) => {
	const { t } = useTranslation('myShipments');
	const dispatch = useDispatch<any>();
	const userProfileReducer: IUserProfileReducer = useSelector<IStore, IUserProfileReducer>(
		store => store.userProfileReducer
	);
	const { userProfile, modalSubmitPending, modalSubmitError } = userProfileReducer;

	const paymentReducer: IPaymentReducer = useSelector<IStore, IPaymentReducer>(store => store.paymentReducer);
	const { paymentCard } = paymentReducer;

	const { locations } = useLocations();
	const [deliveryOption, setDeliveryOption] = useState(new Location());
	const [deliveryOptionAddress, setDeliveryOptionAddress] = useState(new Address());
	useEffect(() => {
		// eslint-disable-next-line no-unused-expressions
		locations?.map(location => {
			if (location.id === userProfile.preferredDeliveryMethodLocationId) {
				setDeliveryOption(location);
				setDeliveryOptionAddress(location.address);
			}
		});
	}, [locations, userProfile]);

	const progress = () => {
		const sessionStoragePhone = window.sessionStorage.getItem('onboardingPhone');
		if (sessionStoragePhone) NewUserProfile.phoneNumber = sessionStoragePhone;
		const sessionStorageEmail = window.sessionStorage.getItem('onboardingEmail');
		if (sessionStorageEmail) NewUserProfile.email = sessionStorageEmail;

		if (NewUserProfile.paymentCardId === 'SET') NewUserProfile.paymentCardId = paymentCard.id;
		NewUserProfile.hasAgreedToGdprTerms = true;
		dispatch(agreeToGdpr(NewUserProfile));
	};

	const submitStepDelivery = () => {
		if (userProfile.preferredDeliveryMethod === LocationType.PackageLabelDecides) return;

		NewUserProfile.preferredDeliveryMethodLocationId = userProfile.preferredDeliveryMethodLocationId;
		NewUserProfile.preferredDeliveryMethod = locations.find(
			e => e.id === NewUserProfile.preferredDeliveryMethodLocationId
		).locationType;
		progress();
	};

	return (
		<StyledOnboarding.LeftColumn>
			<H2>{t('onboarding.delivery.heading')}</H2>

			<StyledOnboarding.InfoText>{t('onboarding.delivery.infoText')}</StyledOnboarding.InfoText>

			{modalSubmitError && (
				<StyledModals.ErrorMessage>
					<FaExclamationCircle />
					<div>{t('genericError')}</div>
				</StyledModals.ErrorMessage>
			)}

			{userProfile.preferredDeliveryMethodLocationId !== null &&
				userProfile.preferredDeliveryMethod !== LocationType.PackageLabelDecides && (
					<StyledOnboarding.LinkCard
						border
						onClick={() => dispatch(setActiveModal(DELIVERY_OPTION_MODAL, ''))}>
						<div>{deliveryOption.name}</div>
						<div>{deliveryOptionAddress.street1}</div>
						<div>
							{deliveryOptionAddress.postalCode}
							{deliveryOptionAddress.city}
						</div>
						<div>
							{t('change')}
							<FaArrowRight />
						</div>
					</StyledOnboarding.LinkCard>
				)}

			{(userProfile.preferredDeliveryMethodLocationId === null ||
				userProfile.preferredDeliveryMethod === LocationType.PackageLabelDecides) && (
				<>
					<StyledOnboarding.LinkCard
						onClick={() => dispatch(setActiveModal(DELIVERY_OPTION_MODAL, 'póstbox'))}>
						<div>{t('postbox')}</div>
						<div>{t('postboxInfo')}</div>
						<div>
							<FaArrowRight />
						</div>
					</StyledOnboarding.LinkCard>
					<StyledOnboarding.LinkCard
						onClick={() => dispatch(setActiveModal(DELIVERY_OPTION_MODAL, 'pakkaport'))}>
						<div>{t('pakkaport')}</div>
						<div>{t('pakkaportInfo')}</div>
						<div>
							<FaArrowRight />
						</div>
					</StyledOnboarding.LinkCard>
					<StyledOnboarding.LinkCard
						onClick={() => dispatch(setActiveModal(DELIVERY_OPTION_MODAL, 'pósthús'))}>
						<div>{t('postoffice')}</div>
						<div>{t('postofficeInfo')}</div>
						<div>
							<FaArrowRight />
						</div>
					</StyledOnboarding.LinkCard>
				</>
			)}

			<Button
				primary
				onClick={submitStepDelivery}
				disabled={userProfile.preferredDeliveryMethod === LocationType.PackageLabelDecides}
				isLoading={modalSubmitPending}>
				{t('onboarding.complete')}
			</Button>

			<StyledOnboarding.SkipLink onClick={progress}>{t('onboarding.skip')}</StyledOnboarding.SkipLink>
		</StyledOnboarding.LeftColumn>
	);
};

export default StepDelivery;
